import React, { useState } from 'react';
import styled from 'styled-components';
import { fieldAnalyticsService } from '../../services/fieldAnalyticsService';
import { TopPlayersResponse } from '../../types/analytics';

interface TopPlayersSectionProps {
  fieldId: string;
  supportedGameTypes: string[];
}

const SectionContainer = styled.div`
  margin-top: 32px;
  padding: 24px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  border: 1px solid rgba(0, 212, 255, 0.15);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SectionTitle = styled.h3`
  color: #00d4ff;
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.3rem;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const TooltipIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 212, 255, 0.2);
  border: 1px solid rgba(0, 212, 255, 0.4);
  color: #00d4ff;
  font-size: 12px;
  font-weight: bold;
  cursor: help;

  &:hover + div {
    opacity: 1;
    visibility: visible;
  }
`;

const TooltipContent = styled.div`
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translateX(-50%);
  margin-top: 8px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  font-weight: normal;
  line-height: 1.5;
  white-space: nowrap;
  z-index: 100;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid rgba(0, 212, 255, 0.3);
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterLabel = styled.label`
  color: #8bb8d8;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
`;

const DateInput = styled.input`
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  font-weight: bold;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  font-weight: bold;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }

  option {
    background: #1a1a2e;
    color: #ffffff;
    font-weight: bold;
  }
`;

const LoadButton = styled.button`
  padding: 8px 20px;
  background: rgba(0, 212, 255, 0.15);
  border: 1px solid #00d4ff;
  border-radius: 6px;
  color: #00d4ff;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;

  &:hover:not(:disabled) {
    background: rgba(0, 212, 255, 0.25);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  max-height: 500px;
  overflow-y: auto;
  margin-top: 16px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.3);
    border-radius: 4px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Courier New', monospace;
`;

const Th = styled.th`
  padding: 12px 8px;
  text-align: left;
  color: #00d4ff;
  font-size: 0.85rem;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
  position: sticky;
  top: 0;
  background: #0d0d1a;
`;

const Td = styled.td`
  padding: 10px 8px;
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: bold;
  border-bottom: 1px solid rgba(0, 212, 255, 0.1);
`;

const RankCell = styled(Td)<{ $rank: number }>`
  font-weight: bold;
  color: ${({ $rank }) => {
    if ($rank === 1) return '#ffd700';
    if ($rank === 2) return '#c0c0c0';
    if ($rank === 3) return '#cd7f32';
    return '#8bb8d8';
  }};
`;

const WinRateCell = styled(Td)<{ $rate: number }>`
  color: ${({ $rate }) => {
    if ($rate >= 70) return '#4caf50';
    if ($rate >= 50) return '#8bc34a';
    if ($rate >= 30) return '#ffc107';
    return '#f44336';
  }};
  font-weight: bold;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 16px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 212, 255, 0.1);
  border-top: 3px solid #00d4ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  color: #00d4ff;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #8bb8d8;
  font-family: 'Courier New', monospace;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #f44336;
  font-family: 'Courier New', monospace;
`;

const WarningMessage = styled.div`
  text-align: center;
  padding: 12px 20px;
  color: #ffc107;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  margin-bottom: 16px;
`;

const InfoText = styled.div`
  color: #8bb8d8;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  margin-top: 8px;
`;

const TopPlayersSection: React.FC<TopPlayersSectionProps> = ({ fieldId, supportedGameTypes }) => {
  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const [dateFrom, setDateFrom] = useState(weekAgo.toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(today.toISOString().split('T')[0]);
  const [gameType, setGameType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TopPlayersResponse | null>(null);

  const [periodWarning, setPeriodWarning] = useState<string | null>(null);

  // Вычисляем максимальную дату To (dateFrom + 90 дней, но не больше сегодня)
  const getMaxDateTo = (fromDate: string) => {
    const from = new Date(fromDate);
    const maxTo = new Date(from);
    maxTo.setDate(maxTo.getDate() + 90);
    const todayDate = new Date();
    return maxTo > todayDate ? todayDate : maxTo;
  };

  // Вычисляем минимальную дату From (dateTo - 90 дней)
  const getMinDateFrom = (toDate: string) => {
    const to = new Date(toDate);
    const minFrom = new Date(to);
    minFrom.setDate(minFrom.getDate() - 90);
    return minFrom;
  };

  const formatDateStr = (date: Date) => date.toISOString().split('T')[0];

  const handleDateFromChange = (newFrom: string) => {
    setDateFrom(newFrom);
    setPeriodWarning(null);

    const from = new Date(newFrom);
    const to = new Date(dateTo);
    const diffDays = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays > 90) {
      // Корректируем dateTo на from + 90 дней
      const maxTo = getMaxDateTo(newFrom);
      setDateTo(formatDateStr(maxTo));
      setPeriodWarning('Maximum period is 90 days. End date adjusted automatically.');
    } else if (to < from) {
      // Если dateTo раньше dateFrom, ставим dateTo = dateFrom
      setDateTo(newFrom);
    }
  };

  const handleDateToChange = (newTo: string) => {
    setDateTo(newTo);
    setPeriodWarning(null);

    const from = new Date(dateFrom);
    const to = new Date(newTo);
    const diffDays = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays > 90) {
      // Корректируем dateFrom на to - 90 дней
      const minFrom = getMinDateFrom(newTo);
      setDateFrom(formatDateStr(minFrom));
      setPeriodWarning('Maximum period is 90 days. Start date adjusted automatically.');
    } else if (from > to) {
      // Если dateFrom позже dateTo, ставим dateFrom = dateTo
      setDateFrom(newTo);
    }
  };

  const handleLoad = async () => {
    setLoading(true);
    setError(null);
    setPeriodWarning(null);

    try {
      const from = new Date(dateFrom);
      const to = new Date(dateTo);

      const result = await fieldAnalyticsService.getTopPlayers(
        fieldId,
        from,
        to,
        gameType === 'all' ? undefined : gameType
      );
      setData(result);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load top players');
    } finally {
      setLoading(false);
    }
  };

  const formatGameType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <SectionContainer>
      <SectionHeader>
        <TitleContainer>
          <SectionTitle>Top 100 Players by Win Rate</SectionTitle>
          <TooltipWrapper>
            <TooltipIcon>?</TooltipIcon>
            <TooltipContent>
              Minimum 10 missions required to qualify<br />
              Sorted by: Win Rate, then Missions Completed<br />
              Maximum period: 90 days
            </TooltipContent>
          </TooltipWrapper>
        </TitleContainer>
        <FiltersContainer>
          <FilterGroup>
            <FilterLabel>From:</FilterLabel>
            <DateInput
              type="date"
              value={dateFrom}
              max={today.toISOString().split('T')[0]}
              onChange={(e) => handleDateFromChange(e.target.value)}
            />
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>To:</FilterLabel>
            <DateInput
              type="date"
              value={dateTo}
              max={today.toISOString().split('T')[0]}
              onChange={(e) => handleDateToChange(e.target.value)}
            />
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>Type:</FilterLabel>
            <Select value={gameType} onChange={(e) => setGameType(e.target.value)}>
              <option value="all">All Types</option>
              {supportedGameTypes.map((type) => (
                <option key={type} value={type}>
                  {formatGameType(type)}
                </option>
              ))}
            </Select>
          </FilterGroup>
          <LoadButton onClick={handleLoad} disabled={loading}>
            {loading ? 'Loading...' : 'Load'}
          </LoadButton>
        </FiltersContainer>
      </SectionHeader>

      {periodWarning && (
        <WarningMessage>{periodWarning}</WarningMessage>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading && (
        <LoadingContainer>
          <Spinner />
          <LoadingText>Loading top players...</LoadingText>
        </LoadingContainer>
      )}

      {!loading && !data && !error && (
        <EmptyState>
          Select a date range and click "Load" to see top players
        </EmptyState>
      )}

      {!loading && data && data.players.length === 0 && (
        <EmptyState>
          No qualified players found for the selected period.
          Players need at least {data.minGamesRequired} games to qualify.
          ({data.totalPlayers} players found, but none with {data.minGamesRequired}+ games)
        </EmptyState>
      )}

      {!loading && data && data.players.length > 0 && (
        <>
          <InfoText>
            Showing {data.players.length} of {data.qualifiedPlayers} qualified players
            ({data.totalPlayers} total, min {data.minGamesRequired} missions required) |
            Game Type: {data.gameType === 'all' ? 'All Types' : formatGameType(data.gameType)} |
            Period: {data.dateFrom} to {data.dateTo}
          </InfoText>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Player</Th>
                  <Th>Missions</Th>
                  <Th>Wins</Th>
                  <Th>Win Rate</Th>
                </tr>
              </thead>
              <tbody>
                {data.players.map((player) => (
                  <tr key={player.userId}>
                    <RankCell $rank={player.rank}>{player.rank}</RankCell>
                    <Td>{player.username}</Td>
                    <Td>{player.gamesPlayed}</Td>
                    <Td>{player.wins}</Td>
                    <WinRateCell $rate={player.winRate}>{player.winRate}%</WinRateCell>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </>
      )}
    </SectionContainer>
  );
};

export default TopPlayersSection;
